/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DoCheck, Inject, Input, NgZone, OnChanges, SimpleChanges} from '@angular/core';
import template from './folder-analysis-page.component.html';
import {VisitorProfileApiService} from './visitor-profile-api.service';
import {FormsModule} from '@angular/forms';
import {AppSharedModule} from '../common/app-shared.module';
import {ChartsModule} from '../common/directives/charts.module';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, ChartsModule],
  selector: 'adn-folder-analysis-page',
  template
})
export class FolderAnalysisPageComponent implements DoCheck, OnChanges {
  @Input() model: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;

  activeTab: 'info' | 'profiles' | 'charts' = 'info';
  browserId = '';
  profile: any = {};
  folderScript = '';

  private lastFolderId: string | undefined;

  constructor(
    private readonly visitorProfileApi: VisitorProfileApiService,
    private ngZone: NgZone
  ) {
  }

  get showNewMessage(): boolean {
    return this.effectiveIsNew || !this.model?.folderId;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
  }

  ngDoCheck(): void {
    const folderId = this.model?.folderId;
    if (folderId !== this.lastFolderId) {
      this.lastFolderId = folderId;
      this.folderScript = folderId ? this.buildFolderScript(folderId) : '';
    }
  }

  selectTab(tab: 'info' | 'profiles' | 'charts'): void {
    this.activeTab = tab;
  }

  runRetrieve(): void {
    this.profile = {};
    if (!this.browserId) {
      this.profile.hasError = true;
      this.profile.errorInfo = 'Specify a browser ID to retrieve a user profile.';
      return;
    }
    this.profile.isPending = true;
    this.visitorProfileApi.getVisitor(this.model.folderId, this.browserId).subscribe({next: (data) => {
      this.ngZone.run(() => {
        this.profile.isPending = false;
        this.profile.results = {...data};
        delete this.profile.results.id;
      });
    }, error: (err: any) => {
      this.ngZone.run(() => {
        this.profile.isPending = false;
        this.profile.hasError = true;
        if (err.status === 404) {
          this.profile.errorInfo = 'Unable to retrieve a profile for ' + this.browserId;
        } else {
          this.profile.errorInfo = err.info;
        }
      });
    }});
  }

  private buildFolderScript(folderId: string): string {
    return `<script src="https://cdn.adnuntius.com/adn.js" async></script>
<script>
    window.adn = window.adn || {};
    adn.calls = adn.calls || [];
    adn.calls.push(function() {
        adn.view('${folderId}');
    });
</script>`;
  }
}
